import { Children, Fragment, useRef, useEffect, useState, useCallback } from 'react';
import { useEventListener } from 'usehooks-ts';

/**
 * Creates an integer array containing numbers from start to stop.
 *
 * @see https://docs.python.org/3/library/stdtypes.html#typesseq-range
 */
const arrayFromRange = (start, stop = null, step) => {
    if (stop === null) {
        stop = start || 0;
        start = 0;
    }
    if (!step) {
        step = stop < start ? -1 : 1;
    }
    const length = Math.max(Math.ceil((stop - start) / step), 0);
    const range = Array(length);
    for (let idx = 0; idx < length; idx++, start += step) {
        range[idx] = start;
    }
    return range;
};

/**
 * Create a space-separated list of HTML class names from given arguments.
 *
 * When argument is an object, all of its properties
 * whose values are evaluating to true are added to the list.
 */
function domClassMerge(...args) {
    const classes = [];
    for (const arg of args) {
        const argType = typeof arg;
        if (argType === 'object') {
            const obj = arg;
            Object.keys(obj).forEach((className) => {
                const value = obj[className];
                if (value) {
                    classes.push(className);
                }
            });
        }
        else if (argType === 'string') {
            classes.push(arg.trim());
        }
        else if (argType === 'number') {
            classes.push(arg);
        }
    }
    return classes.join(' ');
}

function domGetScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    document.body.appendChild(outer);
    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);
    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);
    return scrollbarWidth;
}

/**
 * Convert props.children value to flattened array. Will remove the Fragment if present
 */
function domToChildArray(children, predicate = () => true) {
    return Children.toArray(children)
        .map((item) => ((item === null || item === void 0 ? void 0 : item.type) === Fragment ? item.props.children : item))
        .flat()
        .filter(predicate);
}

const functionNoop = () => undefined;

/**
 * A function return whatever you pass
 */
const functionSelf = (items) => items;

const gitGetChanges = (commandOutput) => {
    var _a, _b, _c;
    const match = commandOutput.match(/\d+/g);
    return {
        fileCount: parseInt((_a = match === null || match === void 0 ? void 0 : match[0]) !== null && _a !== void 0 ? _a : '0', 10),
        insertionCount: parseInt((_b = match === null || match === void 0 ? void 0 : match[1]) !== null && _b !== void 0 ? _b : '0', 10),
        deletionCount: parseInt((_c = match === null || match === void 0 ? void 0 : match[2]) !== null && _c !== void 0 ? _c : '0', 10),
    };
};

const GLOBALTHIS_PF_SPECIFIC_KEY = 'PF_VALUES';

/**
 * @description gets a value from a PF specific object inside the globalThis
 */
function globalThisGetValue(key) {
    if (globalThis[GLOBALTHIS_PF_SPECIFIC_KEY]) {
        return globalThis[GLOBALTHIS_PF_SPECIFIC_KEY][key];
    }
    return null;
}

/**
 * @description sets a value to a PF specific object inside the globalThis
 */
function globalThisSetValue(key, value) {
    if (!globalThis[GLOBALTHIS_PF_SPECIFIC_KEY]) {
        globalThis[GLOBALTHIS_PF_SPECIFIC_KEY] = {};
    }
    globalThis[GLOBALTHIS_PF_SPECIFIC_KEY][key] = value;
}

/**
 * Hook that simulates the class constructor behavior
 * Use it for initialization that need to be done before rendering
 * @param fn Function to be called once on component initialization
 */
// tslint:disable-next-line: ban-types
const useConstructor = (fn) => {
    const hasBeenCalledRef = useRef(false);
    const unmountRef = useRef(() => undefined);
    useEffect(() => {
        return () => {
            if (unmountRef.current) {
                unmountRef.current();
            }
        };
    }, []);
    if (hasBeenCalledRef.current) {
        return;
    }
    const unmount = fn();
    if (unmount) {
        unmountRef.current = unmount;
    }
    hasBeenCalledRef.current = true;
};

/**
 * @description stores values and returns previous one
 * @param value - value to store
 * @returns previous value held
 */
function usePrevious(value) {
    const ref = useRef(null);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

var ScrollDirectionEnum;
(function (ScrollDirectionEnum) {
    ScrollDirectionEnum["UP"] = "up";
    ScrollDirectionEnum["DOWN"] = "down";
})(ScrollDirectionEnum || (ScrollDirectionEnum = {}));

/**
 * Returns users current scroll direction.
 * @param initialDirection initial scroll direction value. Default is down
 * @param thresholdPixels Checks direction change after this threshold has been exceeded.
 * @returns scrollDirection
 */
const useScrollDirection = ({ initialDirection = ScrollDirectionEnum.DOWN, thresholdPixels = 0, }) => {
    const [scrollDirection, setScrollDirection] = useState(initialDirection);
    // Scroll Listeners
    useEffect(() => {
        let lastScrollY = window.scrollY;
        let ticking = false;
        const updateScrollDirection = () => {
            const scrollY = window.scrollY;
            if (Math.abs(scrollY - lastScrollY) < thresholdPixels) {
                // We haven't exceeded the threshold
                ticking = false;
                return;
            }
            setScrollDirection(scrollY > lastScrollY ? ScrollDirectionEnum.DOWN : ScrollDirectionEnum.UP);
            lastScrollY = scrollY > 0 ? scrollY : 0;
            ticking = false;
        };
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateScrollDirection);
                ticking = true;
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [thresholdPixels]);
    return scrollDirection;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var OnKeyDownAllowedKeysEnum;
(function (OnKeyDownAllowedKeysEnum) {
    OnKeyDownAllowedKeysEnum["DOWN"] = "ArrowDown";
    OnKeyDownAllowedKeysEnum["ENTER"] = "Enter";
    OnKeyDownAllowedKeysEnum["ESC"] = "Escape";
    OnKeyDownAllowedKeysEnum["TAB"] = "Tab";
    OnKeyDownAllowedKeysEnum["UP"] = "ArrowUp";
})(OnKeyDownAllowedKeysEnum || (OnKeyDownAllowedKeysEnum = {}));

const allowedKeyValues = Object.values(OnKeyDownAllowedKeysEnum);
const useOnKeyDown = (_a) => {
    var { length } = _a, props = __rest(_a, ["length"]);
    const documentRef = useRef(globalThis.document);
    const [navigated, setNavigated] = useState(-1);
    useEffect(() => {
        var _a;
        if (navigated !== -1) {
            (_a = props.onNavigated) === null || _a === void 0 ? void 0 : _a.call(props, navigated);
        }
    }, [navigated]);
    const onKeyDown = useCallback((event) => {
        var _a, _b, _c;
        if (allowedKeyValues.includes(event.key)) {
            if (event.key !== OnKeyDownAllowedKeysEnum.TAB) {
                event.preventDefault();
                switch (event.key) {
                    case OnKeyDownAllowedKeysEnum.DOWN:
                        if (length !== undefined) {
                            setNavigated((navigated + 1) % length);
                        }
                        break;
                    case OnKeyDownAllowedKeysEnum.UP:
                        if (length !== undefined) {
                            setNavigated(navigated - 1 < 0 ? length - 1 : navigated - 1);
                        }
                        break;
                    case OnKeyDownAllowedKeysEnum.ESC:
                        (_a = props.onEscPress) === null || _a === void 0 ? void 0 : _a.call(props);
                        break;
                    case OnKeyDownAllowedKeysEnum.ENTER:
                        (_b = props.onEnterPress) === null || _b === void 0 ? void 0 : _b.call(props, navigated);
                        break;
                }
            }
            else {
                (_c = props.onTabPress) === null || _c === void 0 ? void 0 : _c.call(props);
            }
        }
    }, [props.onTabPress, props.onEscPress, props.onEnterPress, navigated, setNavigated, length]);
    useEventListener('keydown', onKeyDown, documentRef);
    return { navigated };
};

const useOnFullScreen = ({ openOnLoad, onESCKeyDown, onClose, onOpen, documentBody, documentBodyClassName, }) => {
    const [open, setOpen] = useState(openOnLoad);
    const initialRenderRef = useRef(!openOnLoad);
    useEffect(() => {
        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            return;
        }
        const escListener = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                onESCKeyDown === null || onESCKeyDown === void 0 ? void 0 : onESCKeyDown();
            }
        };
        if (open) {
            documentBody.classList.add(documentBodyClassName);
            documentBody.addEventListener('keydown', escListener);
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        else {
            documentBody.classList.remove(documentBodyClassName);
            documentBody.removeEventListener('keydown', escListener);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
        return () => {
            // cleanup anyway on unmount
            documentBody.classList.remove(documentBodyClassName);
            documentBody.removeEventListener('keydown', escListener);
        };
    }, [open, onClose, onOpen, onESCKeyDown, documentBodyClassName]);
    return {
        open,
        setOpen,
    };
};

const jestCreateSpyObj = (methodNames) => {
    const mocked = methodNames.reduce((result, current) => {
        result[current] = jest.fn();
        return result;
    }, {});
    return mocked;
};

/**
 * Compare two object literals for equivalent properties and values
 * Only support root level sorting
 */
function objectCompare(obj1, obj2) {
    if (typeof obj1 === 'undefined' ||
        typeof obj2 === 'undefined' ||
        obj1 === null ||
        obj2 === null ||
        typeof obj1 !== 'object' ||
        typeof obj2 !== 'object') {
        return obj1 === obj2;
    }
    const sortedObj1 = Object.keys(obj1 || {}).sort().reduce((acc, key) => {
        let value = obj1[key];
        if (Array.isArray(value)) {
            value = value.sort();
        }
        return Object.assign(Object.assign({}, acc), { [key]: value });
    }, {});
    const sortedObj2 = Object.keys(obj2 || {}).sort().reduce((acc, key) => {
        let value = obj2[key];
        if (Array.isArray(value)) {
            value = value.sort();
        }
        return Object.assign(Object.assign({}, acc), { [key]: value });
    }, {});
    return JSON.stringify(sortedObj1) === JSON.stringify(sortedObj2);
}

/**
 * It wraps the Object reduce function
 * @param source The source object
 * @param predicate The reduce function
 * @param target The optional target object
 */
const objectReduce = (source, predicate, target = {}) => {
    return Object.keys(source).reduce((accumulated, current) => {
        return predicate(accumulated, current, source[current]);
    }, target);
};

/**
 * Decodes a Uniform Resource Identifier (URI) previously created by a similar routine
 *
 * @param {QueryParamsType} queryParams Query key-value pairs
 */
const objectFromDecodedQuery = (queryParams) => objectReduce(queryParams, (acc, key, val) => {
    acc[key] = decodeURI(val);
    return acc;
}, {});

function objectToCamelCaseFromSnakeCase(obj) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => objectToCamelCaseFromSnakeCase(item));
    }
    if (typeof obj === 'object') {
        const newObj = {};
        for (const [key, value] of Object.entries(obj)) {
            const newKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            newObj[newKey] = typeof value === 'object' ? objectToCamelCaseFromSnakeCase(value) : value;
        }
        return newObj;
    }
    return obj;
}

/**
 * Parse the string to integer
 * For NaN returns undefined
 * @param input - string to transform
 */
const stringToNumber = (input) => {
    const num = parseInt(input, 10);
    return isNaN(num) ? undefined : num;
};

/**
 * Return platforms that are detected by using user agent from request
 * * @param userAgent User agent string
 */
const userAgentDetectPlatform = (userAgent) => {
    const isAndroid = /Android/i.test(userAgent);
    const isIos = /iPhone|iPad|iPod/i.test(userAgent);
    const isOpera = /Opera Mini/i.test(userAgent);
    const isWindowsPhone = /Windows Phone/i.test(userAgent);
    const isMobile = isAndroid || isIos || isOpera || isWindowsPhone;
    const isWindows = /Windows NT/i.test(userAgent);
    const isMac = /Macintosh/i.test(userAgent);
    const isLinux = /Linux x86_64/i.test(userAgent);
    const isDesktop = isWindows || isMac || isLinux;
    return {
        isDesktop,
        isMobile,
        isAndroid,
        isIos,
    };
};

export { GLOBALTHIS_PF_SPECIFIC_KEY, ScrollDirectionEnum, arrayFromRange, domClassMerge, domGetScrollbarWidth, domToChildArray, functionNoop, functionSelf, gitGetChanges, globalThisGetValue, globalThisSetValue, jestCreateSpyObj, objectCompare, objectFromDecodedQuery, objectReduce, objectToCamelCaseFromSnakeCase, stringToNumber, useConstructor, useOnFullScreen, useOnKeyDown, usePrevious, useScrollDirection, userAgentDetectPlatform };
